import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, ShieldCheck, RefreshCw } from "lucide-react";
import { company, navLinks } from "../mock";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* top bar */}
      <div className="hidden md:block bg-[#0a2b52] text-white/90 text-sm">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between py-2">
          <span className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-[#7db8e6]" />
            {company.legalName} — Surrey, BC
          </span>
          <div className="flex items-center gap-6">
            <a href={`mailto:${company.email}`} className="hover:text-[#7db8e6] transition-colors">
              {company.email}
            </a>
            <a href={`tel:${company.phoneRaw}`} className="flex items-center gap-1.5 font-semibold text-[#7db8e6]">
              <Phone size={15} /> {company.phone}
            </a>
            <Link
              to="/icbc-policy-renewal"
              className="flex items-center gap-1.5 bg-[#0066b3] hover:bg-[#0077cc] text-white font-semibold px-3.5 py-1 rounded-full transition-colors"
            >
              <RefreshCw size={14} /> Renew ICBC Online
            </Link>
          </div>
        </div>
      </div>

      {/* main nav */}
      <div
        className={`transition-all duration-300 ${
          scrolled ? "glass-strong" : "bg-white/70 backdrop-blur-md"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-[68px]">
          <Link to="/" className="flex items-center gap-2.5">
            <img
              src="/assets/logo.png"
              alt="Cloverdale Insurance Services Ltd."
              className="h-10 md:h-11 w-auto"
            />
          </Link>

          {/* desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.label} className="relative group">
                <Link
                  to={link.to}
                  className={`flex items-center gap-1 px-3.5 py-2 rounded-lg text-[15px] font-medium transition-colors ${
                    location.pathname === link.to
                      ? "text-[#0066b3]"
                      : "text-[#0a2b52] hover:text-[#0066b3]"
                  }`}
                >
                  {link.label}
                  {link.children && <ChevronDown size={15} />}
                </Link>
                {link.children && (
                  <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="glass-strong rounded-xl p-2 min-w-[230px]">
                      {link.children.map((c) => (
                        <Link
                          key={c.label}
                          to={c.to}
                          className="block px-3 py-2 rounded-lg text-sm text-[#0a2b52] hover:bg-[#0066b3]/10 hover:text-[#003c7a] transition-colors"
                        >
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              className="brand-btn px-5 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap"
            >
              Get a Free Quote
            </Link>
          </div>

          {/* mobile toggle */}
          <button
            className="lg:hidden text-[#0a2b52] p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* mobile menu */}
        {open && (
          <div className="lg:hidden glass-strong border-t border-white/40 px-6 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="block px-3 py-2.5 rounded-lg font-medium text-[#0a2b52] hover:bg-[#0066b3]/10"
              >
                {link.label}
              </Link>
            ))}
            <a
              href={`tel:${company.phoneRaw}`}
              className="flex items-center gap-2 px-3 py-2.5 font-semibold text-[#0066b3]"
            >
              <Phone size={16} /> {company.phone}
            </a>
            <Link
              to="/contact"
              className="brand-btn block text-center px-5 py-3 rounded-full font-semibold mt-2"
            >
              Get a Free Quote
            </Link>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
